import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import type { AssociationActivityRow, EventPopup } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

type FilterTab = "all" | "association" | "event";

type CombinedItem =
  | { type: "activity"; data: AssociationActivityRow; sortDate: string }
  | { type: "event"; data: EventPopup; sortDate: string };

const ActivitiesPage = () => {
  const [activities, setActivities] = useState<AssociationActivityRow[]>([]);
  const [events, setEvents] = useState<EventPopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const [actRes, evtRes] = await Promise.all([
        supabase
          .from("association_activities")
          .select("id, title, title_kr, subtitle, image_url, category, published_at, is_featured, event_date, event_location")
          .eq("is_published", true)
          .order("published_at", { ascending: false }),
        supabase
          .from("event_popups")
          .select("*")
          .order("is_active", { ascending: false })
          .order("created_at", { ascending: false }),
      ]);
      setActivities((actRes.data as AssociationActivityRow[]) ?? []);
      setEvents((evtRes.data as EventPopup[]) ?? []);
      setLoading(false);
    };

    fetchAll();

    const actCh = supabase
      .channel("activities_page_act")
      .on("postgres_changes", { event: "*", schema: "public", table: "association_activities" }, fetchAll)
      .subscribe();
    const evtCh = supabase
      .channel("activities_page_evt")
      .on("postgres_changes", { event: "*", schema: "public", table: "event_popups" }, fetchAll)
      .subscribe();

    return () => {
      supabase.removeChannel(actCh);
      supabase.removeChannel(evtCh);
    };
  }, []);

  const combined: CombinedItem[] = [
    ...activities.map((a) => ({
      type: "activity" as const,
      data: a,
      sortDate: a.published_at ?? "",
    })),
    ...events.map((e) => ({
      type: "event" as const,
      data: e,
      sortDate: e.created_at ?? "",
    })),
  ].sort((a, b) => b.sortDate.localeCompare(a.sortDate));

  const filtered = combined.filter((item) => {
    const matchTab =
      activeTab === "all" ||
      (activeTab === "association" && item.type === "activity") ||
      (activeTab === "event" && item.type === "event");

    const title =
      item.type === "activity"
        ? (item.data as AssociationActivityRow).title
        : (item.data as EventPopup).title_vi;
    const matchSearch = !search || title.toLowerCase().includes(search.toLowerCase());

    return matchTab && matchSearch;
  });

  const tabs: { id: FilterTab; label: string; icon: string; count: number }[] = [
    { id: "all", label: "Tất cả", icon: "ri-apps-line", count: combined.length },
    { id: "association", label: "Hoạt động hiệp hội", icon: "ri-building-4-line", count: activities.length },
    { id: "event", label: "Sự kiện", icon: "ri-calendar-event-line", count: events.length },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#004A99] to-[#003d7d] text-white py-16">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-white/60 hover:text-white cursor-pointer">Trang chủ</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white/80">Hoạt Động & Sự Kiện</span>
          </div>
          <h1 className="text-3xl font-black mb-3">Hoạt Động Hiệp Hội & Sự Kiện</h1>
          <p className="text-white/70 text-sm max-w-xl leading-relaxed">
            Tổng hợp tin tức, hoạt động và sự kiện của Hiệp Hội Carbon Nano Hàn–Việt
          </p>
          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
              <i className="ri-newspaper-line text-white" />
              <span className="text-sm font-semibold">{activities.length} Hoạt động</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
              <i className="ri-calendar-event-line text-white" />
              <span className="text-sm font-semibold">{events.filter(e => e.is_active).length} Sự kiện đang diễn ra</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        {/* Filter tabs + search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-[#004A99] text-white"
                    : "text-gray-600 hover:text-[#004A99] hover:bg-[#EDF2FB]"
                }`}
              >
                <i className={`${tab.icon} text-xs`} />
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Tìm kiếm..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#004A99] bg-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-3xl text-[#004A99]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <i className="ri-inbox-line text-5xl text-gray-200 block mb-4" />
            <p className="text-gray-500 font-semibold">Không có kết quả nào</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, idx) => (
              <ActivityEventCard key={idx} item={item} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

const ActivityEventCard = ({ item }: { item: CombinedItem }) => {
  const isActivity = item.type === "activity";
  const activity = isActivity ? (item.data as AssociationActivityRow) : null;
  const event = !isActivity ? (item.data as EventPopup) : null;

  const title = isActivity ? activity!.title : event!.title_vi;
  const imageUrl = isActivity ? activity!.image_url : event!.image_url;
  const href = isActivity ? `/hoat-dong-hiep-hoi-va-su-kien/${activity!.id}` : `/su-kien/${event!.id}`;
  const bgColor = event?.bg_color ?? "#004A99";
  const date = isActivity
    ? (activity!.published_at ? new Date(activity!.published_at).toLocaleDateString("vi-VN") : "")
    : (event!.event_date ?? "");
  const subtitle = isActivity ? activity!.subtitle : (event as EventPopup & { short_description_vi?: string }).short_description_vi;

  return (
    <Link
      to={href}
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col hover:border-[#004A99]/30 transition-all cursor-pointer"
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden flex-shrink-0" style={{ backgroundColor: bgColor }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className={`${isActivity ? "ri-newspaper-line" : "ri-calendar-event-fill"} text-white/30 text-5xl`} />
          </div>
        )}
        {/* Type badge */}
        <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full ${
          isActivity ? "bg-[#004A99] text-white" : "bg-green-500 text-white"
        }`}>
          {isActivity ? "Hoạt động HH" : "Sự kiện"}
        </span>
        {/* Active event badge */}
        {!isActivity && event?.is_active && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            Đang diễn ra
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-2 line-clamp-2 group-hover:text-[#004A99] transition-colors">
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs text-gray-500 line-clamp-2 mb-3 leading-relaxed">{subtitle}</p>
        )}
        {date && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-auto">
            <i className="ri-calendar-line text-[#004A99]" />
            {date}
          </div>
        )}
      </div>
    </Link>
  );
};

export default ActivitiesPage;
